(() => {
  'use strict';

  const CONFIG = Object.freeze({
    minPersonalizedViews: 3,
    maxPreferredStyles: 2,
    shopifyCandidateLimit: 10,
    fallbackFetchThreshold: 6,
    targetCandidateCount: 8,
    maxProductsPerBrand: 3,
    secondaryStyleMinProducts: 2,
    secondaryStyleMinRatio: 0.5,
    globalPopularityLimit: 50,
    popularityCollectionHandle: 'best-seller',
    fallbackSectionId: 'sct-recommendation-candidates',
    weights: Object.freeze({
      style: 40,
      brand: 25,
      category: 10,
      price: 10,
      shopify: 10,
      newness: 5,
      popularity: 10,
      coView: 12,
      globalCoView: 15,
    }),
  });

  const query = new URLSearchParams(window.location.search);
  const debug = query.get('sct_debug') === '1';

  const context = readContext();
  const currentProduct = normalizeCurrentProduct(context.product);
  const styleBrandMap = normalizeStyleBrandMap(context.style_brand_map);
  const styleCollectionMap = normalizeStyleCollectionMap(context.style_collection_map);
  const globalPopularityMap = readGlobalPopularityMap();

  let lastResult = null;
  let globalCoViewMap = new Map();
  let globalCoViewRecommendations = [];

  function readContext() {
    const node = document.getElementById('SctRecommendationContext');
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      if (debug) {
        console.warn('[SCT Recommendation Engine] Context parse failed.', error);
      }
      return {};
    }
  }

  function globalCoViewEndpoint() {
    return cleanText(
      context.global_coview_api
    ).replace(/\/$/, '');
  }

  function readGlobalPopularityMap() {
    const node =
      document.getElementById(
        'SctGlobalRankingData'
      );

    if (!node) {
      return new Map();
    }

    try {
      const data =
        JSON.parse(
          node.textContent || '[]'
        );

      if (!Array.isArray(data)) {
        return new Map();
      }

      return new Map(
        data
          .filter(
            item =>
              item &&
              cleanText(item.handle) &&
              toNumber(item.rank) > 0
          )
          .map(
            item => [
              cleanText(item.handle),
              toNumber(item.rank),
            ]
          )
      );
    } catch (error) {
      if (debug) {
        console.warn(
          '[SCT Recommendation Engine] Global popularity parse failed.',
          error
        );
      }

      return new Map();
    }
  }

  function cleanText(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function normalizeCurrentProduct(product) {
    if (!product || typeof product !== 'object') return null;

    return {
      id: product.id ?? null,
      handle: cleanText(product.handle),
      vendor: cleanText(product.vendor),
      category: cleanText(product.category),
      price_cents: toNumber(product.price_cents),
      styles: Array.isArray(product.styles)
        ? product.styles.map(cleanText).filter(Boolean)
        : [],
    };
  }

  function normalizeStyleBrandMap(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(value).map(([style, vendors]) => [
        cleanText(style),
        Array.isArray(vendors)
          ? [...new Set(vendors.map(cleanText).filter(Boolean))]
          : [],
      ])
    );
  }

  function normalizeStyleCollectionMap(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(value)
        .map(([style, handle]) => [cleanText(style), cleanText(handle)])
        .filter(([style, handle]) => style && handle)
    );
  }

  function getProfile() {
    return window.SctRecommendation?.getProfile?.() || null;
  }

  function stylesForVendor(vendor) {
    const cleanVendor = cleanText(vendor);
    if (!cleanVendor) return [];

    return Object.entries(styleBrandMap)
      .filter(([, vendors]) => vendors.includes(cleanVendor))
      .map(([style]) => style);
  }

  function normalizeCandidate(product, index, total, source = 'shopify', sourceStyle = '') {
    if (!product || typeof product !== 'object') return null;

    const handle = cleanText(product.handle);
    if (!handle) return null;

    const vendor = cleanText(product.vendor);
    const category = cleanText(
      product.product_type ||
      product.type ||
      product.category
    );

    let image = cleanText(product.image);

    if (!image && typeof product.featured_image === 'string') {
      image = product.featured_image;
    } else if (
      !image &&
      product.featured_image &&
      typeof product.featured_image === 'object'
    ) {
      image =
        product.featured_image.src ||
        product.featured_image.url ||
        '';
    }

    const rawPrice =
      product.price_cents !== undefined
        ? product.price_cents
        : product.price;

    return {
      id: product.id ?? null,
      handle,
      title: cleanText(product.title),
      vendor,
      category,
      price_cents: toNumber(rawPrice),
      available: product.available !== false,
      created_at: product.created_at || null,
      url: product.url || `/products/${handle}`,
      image,
      styles: stylesForVendor(vendor),
      source,
      source_style: cleanText(sourceStyle),
      shopify_rank: source === 'shopify' ? index + 1 : 0,
      shopify_total: source === 'shopify' ? total : 0,
      global_rank:
        toNumber(
          globalPopularityMap.get(handle)
        ),
    };
  }

  function sortedScores(map) {
    return Object.entries(map || {})
      .filter(([, score]) => toNumber(score) > 0)
      .sort((a, b) => toNumber(b[1]) - toNumber(a[1]));
  }

  function maxScore(map) {
    const entries = sortedScores(map);
    return entries.length ? toNumber(entries[0][1]) : 0;
  }

  function ratio(value, maximum) {
    const number = toNumber(value);
    const max = toNumber(maximum);

    if (number <= 0 || max <= 0) return 0;

    return Math.max(0, Math.min(1, number / max));
  }

  function preferredStyles(profile) {
    const learned =
      sortedScores(
        profile?.styles
      )
        .slice(
          0,
          CONFIG.maxPreferredStyles
        )
        .map(
          ([style]) => style
        );

    if (learned.length) {
      return learned;
    }

    return Array.isArray(
      currentProduct?.styles
    )
      ? currentProduct.styles
          .slice(
            0,
            CONFIG.maxPreferredStyles
          )
      : [];
  }

  function weightsForProfile(
    profile
  ) {
    const viewed =
      toNumber(
        profile
          ?.uniqueViewedProducts
      );

    if (viewed <= 1) {
      return {
        style: 50,
        brand: 0,
        category: 0,
        price: 0,
        shopify: 20,
        newness: 10,
        popularity: 20,
        coView: 0,
        globalCoView: 15,
      };
    }

    if (viewed === 2) {
      return {
        style: 40,
        brand: 15,
        category: 8,
        price: 7,
        shopify: 12,
        newness: 8,
        popularity: 10,
        coView: 9,
        globalCoView: 15,
      };
    }

    return CONFIG.weights;
  }

  function priceMatch(priceCents, preferredPriceCents) {
    const price = toNumber(priceCents);
    const preferred = toNumber(preferredPriceCents);

    if (price <= 0 || preferred <= 0) return 0;

    const differenceRatio =
      Math.abs(price - preferred) / preferred;

    return Math.max(0, 1 - Math.min(1, differenceRatio));
  }

  function newnessMatch(createdAt) {
    if (!createdAt) return 0;

    const timestamp = Date.parse(createdAt);
    if (!Number.isFinite(timestamp)) return 0;

    const ageDays =
      (Date.now() - timestamp) /
      (1000 * 60 * 60 * 24);

    if (ageDays <= 30) return 1;
    if (ageDays <= 90) return 0.5;

    return 0;
  }

  function popularityMatch(handle) {
    const rank =
      toNumber(
        globalPopularityMap.get(
          cleanText(handle)
        )
      );

    if (
      rank <= 0 ||
      rank >
        CONFIG.globalPopularityLimit
    ) {
      return 0;
    }

    return Math.max(
      0,
      Math.min(
        1,
        (
          CONFIG.globalPopularityLimit +
          1 -
          rank
        ) /
        CONFIG.globalPopularityLimit
      )
    );
  }

  function globalCoViewMatch(handle) {
    const item =
      globalCoViewMap.get(
        cleanText(handle)
      );

    if (!item) {
      return 0;
    }

    const values =
      [...globalCoViewMap.values()];

    const maxCount =
      values.reduce(
        (best, value) =>
          Math.max(
            best,
            toNumber(
              value?.count
            )
          ),
        0
      );

    const countRatio =
      maxCount > 0
        ? ratio(
            item.count,
            maxCount
          )
        : 0;

    const total =
      Math.max(
        1,
        toNumber(
          item.total
        )
      );

    const rankRatio =
      Math.max(
        0,
        Math.min(
          1,
          (
            total -
            toNumber(
              item.rank
            ) +
            1
          ) /
          total
        )
      );

    const sourceFactor =
      item.source ===
        'market'
        ? 1
        : 0.85;

    /*
     * 1回だけの偶然の遷移を
     * 強く効かせすぎない。
     * count 5以上で最大信頼。
     */
    const confidenceFactor =
      Math.min(
        1,
        toNumber(
          item.count
        ) / 5
      );

    let recency = 0.5;

    const timestamp =
      Date.parse(
        item.last_seen_at ||
        ''
      );

    if (
      Number.isFinite(
        timestamp
      )
    ) {
      const ageDays =
        (
          Date.now() -
          timestamp
        ) /
        (
          1000 *
          60 *
          60 *
          24
        );

      if (ageDays <= 30) {
        recency = 1;
      } else if (
        ageDays <= 90
      ) {
        recency = 0.8;
      } else if (
        ageDays <= 180
      ) {
        recency = 0.6;
      }
    }

    return Math.max(
      0,
      Math.min(
        1,
        (
          countRatio * 0.6 +
          rankRatio * 0.4
        ) *
        sourceFactor *
        confidenceFactor *
        recency
      )
    );
  }

  function coViewMatch(
    sourceHandle,
    targetHandle
  ) {
    const edge =
      window
        .SctRecommendation
        ?.getCoViewEdge?.(
          sourceHandle,
          targetHandle
        );

    if (!edge) {
      return 0;
    }

    const rawScore =
      toNumber(
        edge.score
      );

    if (rawScore <= 0) {
      return 0;
    }

    const confidence =
      Math.min(
        1,
        rawScore / 2
      );

    const lastAt =
      toNumber(
        edge.lastAt
      );

    let recency = 0.25;

    if (lastAt > 0) {
      const ageDays =
        (
          Date.now() -
          lastAt
        ) /
        (
          1000 *
          60 *
          60 *
          24
        );

      if (ageDays <= 30) {
        recency = 1;
      } else if (
        ageDays <= 90
      ) {
        recency = 0.75;
      } else if (
        ageDays <= 180
      ) {
        recency = 0.5;
      }
    }

    return Math.max(
      0,
      Math.min(
        1,
        confidence *
        recency
      )
    );
  }

  function hasViewedProduct(profile, handle) {
    const item = profile?.products?.[handle];

    if (!item || typeof item !== 'object') return false;

    return (
      toNumber(item.viewCredits) > 0 ||
      toNumber(item.clickCredits) > 0 ||
      toNumber(item.addToCartCredits) > 0 ||
      toNumber(item.purchaseCredits) > 0
    );
  }

  function scoreCandidate(candidate, profile, index, total) {
    const activeStyles = preferredStyles(profile);

    const activeWeights =
      weightsForProfile(
        profile
      );

    const matchingStyles = candidate.styles.filter(style =>
      activeStyles.includes(style)
    );

    const brandAffinity =
      toNumber(profile?.brands?.[candidate.vendor]);

    const hasStyleSignal =
      activeStyles.length > 0;

    if (!candidate.available) {
      return { eligible: false, reason: 'unavailable' };
    }

    if (
      currentProduct?.handle &&
      candidate.handle === currentProduct.handle
    ) {
      return { eligible: false, reason: 'current_product' };
    }

    if (hasViewedProduct(profile, candidate.handle)) {
      return { eligible: false, reason: 'already_viewed' };
    }

    if (
      hasStyleSignal &&
      matchingStyles.length === 0 &&
      brandAffinity <= 0
    ) {
      return { eligible: false, reason: 'style_gate' };
    }

    const styleRaw = matchingStyles.reduce(
      (best, style) =>
        Math.max(
          best,
          toNumber(profile?.styles?.[style])
        ),
      0
    );

    const styleMaximum =
      maxScore(
        profile?.styles
      );

    const styleRatio =
      styleMaximum > 0
        ? ratio(
            styleRaw,
            styleMaximum
          )
        : matchingStyles.length
          ? 1
          : 0;

    const brandRatio =
      ratio(
        brandAffinity,
        maxScore(profile?.brands)
      );

    const categoryRatio =
      ratio(
        profile?.categories?.[candidate.category],
        maxScore(profile?.categories)
      );

    const priceRatio =
      priceMatch(
        candidate.price_cents,
        profile?.price?.averageCents
      );

    const shopifyRatio =
      candidate.source === 'shopify' && total > 0
        ? Math.max(
            0,
            Math.min(
              1,
              (total - index) / total
            )
          )
        : 0;

    const newnessRatio =
      newnessMatch(candidate.created_at);

    const popularityRatio =
      popularityMatch(
        candidate.handle
      );

    const coViewRatio =
      currentProduct
        ?.handle
        ? coViewMatch(
            currentProduct.handle,
            candidate.handle
          )
        : 0;

    const globalCoViewRatio =
      globalCoViewMatch(
        candidate.handle
      );

    const components = {
      style:
        styleRatio *
        activeWeights.style,
      brand:
        brandRatio *
        activeWeights.brand,
      category:
        categoryRatio *
        activeWeights.category,
      price:
        priceRatio *
        activeWeights.price,
      shopify:
        shopifyRatio *
        activeWeights.shopify,
      newness:
        newnessRatio *
        activeWeights.newness,
      popularity:
        popularityRatio *
        activeWeights.popularity,
      coView:
        coViewRatio *
        activeWeights.coView,
      globalCoView:
        globalCoViewRatio *
        activeWeights.globalCoView,
    };

    const score =
      Object.values(components)
        .reduce(
          (sum, value) =>
            sum + toNumber(value),
          0
        );

    return {
      eligible: true,
      score:
        Math.round(score * 100) / 100,
      components:
        Object.fromEntries(
          Object.entries(components)
            .map(
              ([key, value]) => [
                key,
                Math.round(value * 100) / 100,
              ]
            )
        ),
      matchingStyles,
    };
  }

  async function fetchGlobalCoViewRecommendations() {
    const api =
      globalCoViewEndpoint();

    if (
      !api ||
      !currentProduct?.handle
    ) {
      return [];
    }

    const market =
      cleanText(
        context.market
      ) ||
      cleanText(
        context.country
      ) ||
      'global';

    const url =
      new URL(
        api + '/recommend'
      );

    url.searchParams.set(
      'product',
      currentProduct.handle
    );

    url.searchParams.set(
      'market',
      market
    );

    url.searchParams.set(
      'limit',
      '10'
    );

    const response =
      await fetch(
        url.toString(),
        {
          method:
            'GET',

          mode:
            'cors',

          headers: {
            Accept:
              'application/json',
          },
        }
      );

    if (!response.ok) {
      throw new Error(
        `Global Co-view API returned ${response.status}`
      );
    }

    const data =
      await response.json();

    const recommendations =
      Array.isArray(
        data
          ?.recommendations
      )
        ? data.recommendations
        : [];

    const normalized =
      recommendations
        .map(
          (item, index) => ({
            handle:
              cleanText(
                item?.product
              ),

            count:
              toNumber(
                item?.count
              ),

            source:
              cleanText(
                item?.source
              ),

            last_seen_at:
              item
                ?.last_seen_at ||
              null,

            rank:
              index + 1,

            total:
              recommendations.length,
          })
        )
        .filter(
          item =>
            item.handle &&
            item.handle !==
              currentProduct.handle
        );

    globalCoViewRecommendations =
      normalized;

    globalCoViewMap =
      new Map(
        normalized.map(
          item => [
            item.handle,
            item,
          ]
        )
      );

    return normalized;
  }

  async function fetchProductCandidateByHandle(
    handle,
    index,
    total
  ) {
    const cleanHandle =
      cleanText(handle);

    if (!cleanHandle) {
      return null;
    }

    const root =
      window.Shopify
        ?.routes
        ?.root ||
      '/';

    const url =
      `${root}products/${encodeURIComponent(cleanHandle)}?section_id=sct-recommendation-product-data`;

    const response =
      await fetch(
        url,
        {
          credentials:
            'same-origin',

          headers: {
            Accept:
              'text/html',
          },
        }
      );

    if (!response.ok) {
      return null;
    }

    const html =
      await response.text();

    const documentFragment =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    const dataNode =
      documentFragment
        .querySelector(
          '[data-sct-rec-product-data]'
        );

    if (!dataNode) {
      return null;
    }

    try {
      const parsed =
        JSON.parse(
          dataNode.textContent ||
          '{}'
        );

      return normalizeCandidate(
        parsed,
        index,
        total,
        'global_coview'
      );
    } catch (_error) {
      return null;
    }
  }

  async function fetchGlobalCoViewCandidateProducts(
    recommendations,
    existingCandidates
  ) {
    const existing =
      new Set(
        (
          existingCandidates ||
          []
        )
          .map(
            candidate =>
              cleanText(
                candidate?.handle
              )
          )
          .filter(Boolean)
      );

    const missing =
      (recommendations || [])
        .filter(
          item =>
            item.handle &&
            !existing.has(
              item.handle
            )
        )
        .slice(0, 4);

    if (!missing.length) {
      return [];
    }

    const results =
      await Promise.all(
        missing.map(
          (item, index) =>
            fetchProductCandidateByHandle(
              item.handle,
              index,
              missing.length
            )
        )
      );

    return results
      .filter(Boolean);
  }

  async function fetchShopifyCandidates() {
    if (!currentProduct?.id) {
      return [];
    }

    const root =
      window.Shopify?.routes?.root ||
      '/';

    const params =
      new URLSearchParams({
        product_id:
          String(currentProduct.id),
        limit:
          String(CONFIG.shopifyCandidateLimit),
        intent:
          'related',
      });

    const url =
      `${root}recommendations/products.json?${params.toString()}`;

    const response =
      await fetch(
        url,
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        }
      );

    if (!response.ok) {
      throw new Error(
        `Shopify Recommendation API returned ${response.status}`
      );
    }

    const data =
      await response.json();

    const products =
      Array.isArray(data?.products)
        ? data.products
        : [];

    return products
      .map(
        (product, index) =>
          normalizeCandidate(
            product,
            index,
            products.length,
            'shopify'
          )
      )
      .filter(Boolean);
  }

  async function fetchStyleCandidates(style) {
    const handle =
      styleCollectionMap[style];

    if (!handle) {
      return [];
    }

    const root =
      window.Shopify?.routes?.root ||
      '/';

    const url =
      `${root}collections/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(CONFIG.fallbackSectionId)}`;

    const response =
      await fetch(
        url,
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
          },
        }
      );

    if (!response.ok) {
      throw new Error(
        `Style collection fetch returned ${response.status} for ${style}`
      );
    }

    const html =
      await response.text();

    const documentFragment =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    const dataNode =
      documentFragment
        .querySelector(
          '[data-sct-rec-candidates]'
        );

    if (!dataNode) {
      return [];
    }

    let products = [];

    try {
      const parsed =
        JSON.parse(
          dataNode.textContent || '[]'
        );

      products =
        Array.isArray(parsed)
          ? parsed
          : [];
    } catch (error) {
      throw new Error(
        `Style collection JSON parse failed for ${style}`
      );
    }

    return products
      .map(
        (product, index) =>
          normalizeCandidate(
            product,
            index,
            products.length,
            'style_collection',
            style
          )
      )
      .filter(Boolean);
  }

  async function fetchPopularityCandidates() {
    const handle =
      CONFIG
        .popularityCollectionHandle;

    if (!handle) {
      return [];
    }

    const root =
      window.Shopify
        ?.routes
        ?.root ||
      '/';

    const url =
      `${root}collections/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(CONFIG.fallbackSectionId)}`;

    const response =
      await fetch(
        url,
        {
          credentials:
            'same-origin',

          headers: {
            Accept:
              'text/html',
          },
        }
      );

    if (!response.ok) {
      throw new Error(
        `Popularity collection fetch returned ${response.status}`
      );
    }

    const html =
      await response.text();

    const documentFragment =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    const dataNode =
      documentFragment
        .querySelector(
          '[data-sct-rec-candidates]'
        );

    if (!dataNode) {
      return [];
    }

    let products = [];

    try {
      const parsed =
        JSON.parse(
          dataNode.textContent ||
          '[]'
        );

      products =
        Array.isArray(parsed)
          ? parsed
          : [];
    } catch (_error) {
      throw new Error(
        'Popularity collection JSON parse failed'
      );
    }

    return products
      .map(
        (product, index) =>
          normalizeCandidate(
            product,
            index,
            products.length,
            'global_popularity'
          )
      )
      .filter(Boolean);
  }

  function mergeCandidates(...groups) {
    const merged = new Map();

    groups
      .flat()
      .filter(Boolean)
      .forEach(candidate => {
        if (!candidate?.handle) return;

        const existing =
          merged.get(candidate.handle);

        if (!existing) {
          merged.set(
            candidate.handle,
            candidate
          );
          return;
        }

        if (
          existing.source !== 'shopify' &&
          candidate.source === 'shopify'
        ) {
          merged.set(
            candidate.handle,
            candidate
          );
        }
      });

    return [...merged.values()];
  }

  function rankCandidates(candidates, profile) {
    const eligible = [];
    const excluded = [];

    candidates.forEach(
      (candidate, index) => {
        const scored =
          scoreCandidate(
            candidate,
            profile,
            index,
            candidates.length
          );

        if (!scored.eligible) {
          excluded.push({
            candidate,
            reason:
              scored.reason ||
              'ineligible',
          });

          return;
        }

        eligible.push({
          ...candidate,
          score:
            scored.score,
          score_components:
            scored.components,
          matching_styles:
            scored.matchingStyles,
        });
      }
    );

    eligible.sort(
      (a, b) =>
        b.score - a.score
    );

    return {
      eligible,
      excluded,
    };
  }

  function secondaryStyleTarget(profile) {
    const activeStyles =
      preferredStyles(profile);

    if (activeStyles.length < 2) {
      return {
        style: '',
        required: 0,
      };
    }

    const primaryScore =
      toNumber(
        profile?.styles?.[activeStyles[0]]
      );

    const secondaryScore =
      toNumber(
        profile?.styles?.[activeStyles[1]]
      );

    if (
      primaryScore <= 0 ||
      secondaryScore <= 0 ||
      secondaryScore / primaryScore <
        CONFIG.secondaryStyleMinRatio
    ) {
      return {
        style: '',
        required: 0,
      };
    }

    return {
      style: activeStyles[1],
      required:
        CONFIG.secondaryStyleMinProducts,
    };
  }

  function countStyleMatches(products, style) {
    if (!style) return 0;

    return products.filter(
      product =>
        Array.isArray(product.styles) &&
        product.styles.includes(style)
    ).length;
  }

  function selectDiverseProducts(products, profile) {
    const ranked =
      Array.isArray(products)
        ? products
        : [];

    const selected = [];
    const selectedHandles =
      new Set();

    const brandCounts =
      new Map();

    const addProduct =
      product => {
        if (
          !product ||
          !product.handle ||
          selectedHandles.has(
            product.handle
          )
        ) {
          return false;
        }

        const brand =
          cleanText(product.vendor) ||
          '__NO_BRAND__';

        const brandCount =
          brandCounts.get(brand) || 0;

        if (
          brandCount >=
          CONFIG.maxProductsPerBrand
        ) {
          return false;
        }

        selected.push(product);

        selectedHandles.add(
          product.handle
        );

        brandCounts.set(
          brand,
          brandCount + 1
        );

        return true;
      };

    const secondary =
      secondaryStyleTarget(profile);

    if (
      secondary.style &&
      secondary.required > 0
    ) {
      const activeStyles =
        preferredStyles(profile);

      const primaryStyle =
        activeStyles[0] || '';

      const secondaryExclusive =
        ranked.filter(
          product =>
            Array.isArray(product.styles) &&
            product.styles.includes(
              secondary.style
            ) &&
            (
              !primaryStyle ||
              !product.styles.includes(
                primaryStyle
              )
            )
        );

      const secondaryAny =
        ranked.filter(
          product =>
            Array.isArray(product.styles) &&
            product.styles.includes(
              secondary.style
            )
        );

      for (
        const group
        of [
          secondaryExclusive,
          secondaryAny,
        ]
      ) {
        for (const product of group) {
          if (
            countStyleMatches(
              selected,
              secondary.style
            ) >= secondary.required
          ) {
            break;
          }

          addProduct(product);
        }

        if (
          countStyleMatches(
            selected,
            secondary.style
          ) >= secondary.required
        ) {
          break;
        }
      }
    }

    for (const product of ranked) {
      if (
        selected.length >=
        CONFIG.targetCandidateCount
      ) {
        break;
      }

      addProduct(product);
    }

    selected.sort(
      (a, b) =>
        b.score - a.score
    );

    return selected.slice(
      0,
      CONFIG.targetCandidateCount
    );
  }

  function needsMoreDiversity(products, profile) {
    if (
      products.length <
      CONFIG.fallbackFetchThreshold
    ) {
      return true;
    }

    const secondary =
      secondaryStyleTarget(profile);

    if (
      secondary.style &&
      countStyleMatches(
        products,
        secondary.style
      ) < secondary.required
    ) {
      return true;
    }

    return false;
  }

  function fallbackStylesOrder(profile, selectedProducts) {
    const activeStyles =
      preferredStyles(profile);

    const secondary =
      secondaryStyleTarget(profile);

    if (
      secondary.style &&
      countStyleMatches(
        selectedProducts,
        secondary.style
      ) < secondary.required
    ) {
      return [
        secondary.style,
        ...activeStyles.filter(
          style =>
            style !== secondary.style
        ),
      ];
    }

    return activeStyles;
  }

  function recommendationMode(profile) {
    const uniqueViewed =
      toNumber(
        profile?.uniqueViewedProducts
      );

    if (
      uniqueViewed >=
      CONFIG.minPersonalizedViews
    ) {
      return 'personalized';
    }

    if (uniqueViewed === 2) {
      return 'light';
    }

    return 'cold';
  }

  async function recommend() {
    const profile = getProfile();

    if (!profile) {
      return {
        mode: 'unavailable',
        products: [],
        excluded: [],
      };
    }

    const errors = [];
    const fetchedStyles = [];
    const activeStyles =
      preferredStyles(profile);

    const mode =
      recommendationMode(
        profile
      );

    let popularityCandidatesFetched = false;
    let globalCoViewFetched = false;
    let globalCoViewCandidateCount = 0;

    globalCoViewMap =
      new Map();

    globalCoViewRecommendations =
      [];

    let candidates = [];

    try {
      candidates =
        await fetchShopifyCandidates();
    } catch (error) {
      errors.push(
        error?.message ||
        'shopify_candidate_fetch_failed'
      );

      if (debug) {
        console.warn(
          '[SCT Recommendation Engine] Shopify candidates failed.',
          error
        );
      }
    }

    try {
      const globalRecommendations =
        await fetchGlobalCoViewRecommendations();

      globalCoViewFetched =
        globalRecommendations.length > 0;

      if (
        globalRecommendations.length
      ) {
        const globalCandidates =
          await fetchGlobalCoViewCandidateProducts(
            globalRecommendations,
            candidates
          );

        globalCoViewCandidateCount =
          globalCandidates.length;

        candidates =
          mergeCandidates(
            candidates,
            globalCandidates
          );
      }
    } catch (error) {
      errors.push(
        error?.message ||
        'global_coview_fetch_failed'
      );

      if (debug) {
        console.warn(
          '[SCT Recommendation Engine] Global Co-view failed.',
          error
        );
      }
    }

    if (
      mode !== 'personalized' &&
      activeStyles.length > 0
    ) {
      try {
        const popularityCandidates =
          await fetchPopularityCandidates();

        popularityCandidatesFetched =
          popularityCandidates.length > 0;

        candidates =
          mergeCandidates(
            candidates,
            popularityCandidates
          );
      } catch (error) {
        errors.push(
          error?.message ||
          'popularity_candidate_fetch_failed'
        );

        if (debug) {
          console.warn(
            '[SCT Recommendation Engine] Popularity candidates failed.',
            error
          );
        }
      }
    }

    let ranked =
      rankCandidates(
        candidates,
        profile
      );

    let selectedProducts =
      selectDiverseProducts(
        ranked.eligible,
        profile
      );

    if (
      needsMoreDiversity(
        selectedProducts,
        profile
      ) &&
      activeStyles.length > 0
    ) {
      const stylesToFetch =
        fallbackStylesOrder(
          profile,
          selectedProducts
        );

      for (const style of stylesToFetch) {
        try {
          const styleCandidates =
            await fetchStyleCandidates(style);

          fetchedStyles.push(style);

          candidates =
            mergeCandidates(
              candidates,
              styleCandidates
            );

          ranked =
            rankCandidates(
              candidates,
              profile
            );

          selectedProducts =
            selectDiverseProducts(
              ranked.eligible,
              profile
            );

          if (
            selectedProducts.length >=
              CONFIG.targetCandidateCount &&
            !needsMoreDiversity(
              selectedProducts,
              profile
            )
          ) {
            break;
          }
        } catch (error) {
          errors.push(
            error?.message ||
            `style_candidate_fetch_failed:${style}`
          );

          if (debug) {
            console.warn(
              `[SCT Recommendation Engine] Style fallback failed: ${style}`,
              error
            );
          }
        }
      }
    }

    lastResult = {
      mode,
      generatedAt:
        new Date().toISOString(),
      activeStyles,
      fetchedStyles,
      candidateCount:
        candidates.length,
      globalPopularityProducts:
        globalPopularityMap.size,
      popularityCandidatesFetched,
      globalCoViewFetched,
      globalCoViewCandidateCount,
      globalCoViewRecommendationCount:
        globalCoViewRecommendations.length,
      diversity: {
        maxProductsPerBrand:
          CONFIG.maxProductsPerBrand,
        secondaryStyle:
          secondaryStyleTarget(profile).style,
        secondaryStyleRequired:
          secondaryStyleTarget(profile).required,
      },
      activeWeights:
        weightsForProfile(
          profile
        ),
      profileSummary: {
        uniqueViewedProducts:
          toNumber(
            profile.uniqueViewedProducts
          ),
        preferredPriceCents:
          toNumber(
            profile?.price
              ?.averageCents
          ),
      },
      products:
        selectedProducts,
      excluded:
        ranked.excluded,
      errors,
    };

    if (debug) {
      debugResult(lastResult);
    }

    return lastResult;
  }

  function debugResult(result) {
    console.groupCollapsed(
      '[SCT Recommendation Engine] Ranked candidates'
    );

    console.log(
      'Mode:',
      result.mode
    );

    console.log(
      'Active Style Gate:',
      result.activeStyles
    );

    console.log(
      'Fallback styles fetched:',
      result.fetchedStyles
    );

    console.log(
      'Candidate pool:',
      result.candidateCount
    );

    console.log(
      'Popularity candidates fetched:',
      result.popularityCandidatesFetched
    );

    console.log(
      'Active weights:',
      result.activeWeights
    );

    console.log(
      'Global Co-view:',
      {
        fetched:
          result.globalCoViewFetched,
        recommendations:
          result.globalCoViewRecommendationCount,
        addedCandidates:
          result.globalCoViewCandidateCount,
      }
    );

    if (result.errors.length) {
      console.log(
        'Errors:',
        result.errors
      );
    }

    console.table(
      result.products.map(
        product => ({
          score:
            product.score,
          source:
            product.source,
          source_style:
            product.source_style,
          title:
            product.title,
          brand:
            product.vendor,
          styles:
            product.styles.join(', '),
          category:
            product.category,
          price:
            product.price_cents,
          global_rank:
            product.global_rank,
          style_score:
            product
              .score_components
              .style,
          brand_score:
            product
              .score_components
              .brand,
          category_score:
            product
              .score_components
              .category,
          price_score:
            product
              .score_components
              .price,
          shopify_score:
            product
              .score_components
              .shopify,
          newness_score:
            product
              .score_components
              .newness,
          popularity_score:
            product
              .score_components
              .popularity,
          coview_score:
            product
              .score_components
              .coView,
          global_coview_score:
            product
              .score_components
              .globalCoView,
        })
      )
    );

    if (result.excluded.length) {
      console.table(
        result.excluded.map(
          item => ({
            title:
              item.candidate.title,
            brand:
              item.candidate.vendor,
            styles:
              item.candidate
                .styles
                .join(', '),
            source:
              item.candidate.source,
            reason:
              item.reason,
          })
        )
      );
    }

    console.groupEnd();
  }

  window.SctRecommendationEngine =
    Object.freeze({
      version: 7,

      recommend,

      getLastResult() {
        return lastResult;
      },

      getStyleBrandMap() {
        return styleBrandMap;
      },

      getStyleCollectionMap() {
        return styleCollectionMap;
      },

      getPreferredStyles() {
        return preferredStyles(
          getProfile()
        );
      },
    });

  if (
    debug &&
    toNumber(
      getProfile()
        ?.uniqueViewedProducts
    ) >=
      CONFIG.minPersonalizedViews
  ) {
    recommend();
  }
})();